import React, { useState } from 'react';
import { Mail, Lock, BookOpen, Star, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input, Card } from '../ui';
import { getAuthErrorMessage } from '../../utils/errorHandler';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
    onToggleMode: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');

    const { signIn, resetPassword } = useAuth();
    const navigate = useNavigate();


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await signIn(email, password);
            // After successful login, redirect to dashboard or attempted location
            const attemptedLocation = sessionStorage.getItem('attemptedLocation');
            if (attemptedLocation && attemptedLocation !== '/login') {
                navigate(attemptedLocation, { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        } catch (error: any) {
            setError(getAuthErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };


    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setForgotPasswordLoading(true);
        setForgotPasswordMessage('');
        setError('');

        try {
            await resetPassword(email);
            setForgotPasswordMessage('Password reset email sent! Check your inbox and follow the instructions to reset your password.');
        } catch (error: any) {
            setError(getAuthErrorMessage(error));
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    return (
        <div className="w-full max-w-md mx-auto">
            {/* Islamic School Header */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg animate-glow">
                            <BookOpen className="h-8 w-8 text-white" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center animate-float">
                            <Star className="h-3 w-3 text-white" />
                        </div>
                    </div>
                </div>
                <h2 className="text-4xl font-bold gradient-text mb-2">Noor-e-Hamza</h2>
                <h2 className="text-2xl font-bold gradient-text mb-2">Islamic College</h2>
                <p className="text-gray-600 font-medium">Knowledge • Wisdom • Excellence</p>
                <div className="mt-2 text-sm text-emerald-600 font-medium">Sign in to your account</div>
            </div>

            <Card glassy={true} className="islamic-pattern">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 text-red-600 px-4 py-3 rounded-xl shadow-sm">
                            {error}
                        </div>
                    )}

                    <Input
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        icon={<Mail className="h-5 w-5" />}
                        required
                    />

                    <Input
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        icon={<Lock className="h-5 w-5" />}
                        showPasswordToggle={true}
                        required
                    />

                    <Button
                        type="submit"
                        loading={loading}
                        fullWidth
                        size="lg"
                        className="mt-8"
                    >
                        {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                </form>

                <div className="mt-8 pt-6 border-t border-white/20 text-center space-y-4">
                    <button
                        onClick={() => setShowForgotPassword(true)}
                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors duration-200 block mx-auto"
                    >
                        Forgot your password?
                    </button>

                    <p className="text-gray-600 font-medium">
                        Don't have an account?{' '}
                        <button
                            onClick={onToggleMode}
                            className="gradient-text hover:underline font-semibold transition-all duration-200"
                        >
                            Sign up here
                        </button>
                    </p>
                </div>
            </Card>

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <Card glassy={true} className="islamic-pattern w-full max-w-md">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Reset Password</h3>
                            <button
                                onClick={() => setShowForgotPassword(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={handleForgotPassword} className="space-y-4">
                            {forgotPasswordMessage && (
                                <div className="bg-green-50/80 backdrop-blur-sm border border-green-200 text-green-600 px-4 py-3 rounded-xl shadow-sm">
                                    {forgotPasswordMessage}
                                </div>
                            )}

                            <Input
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                icon={<Mail className="h-5 w-5" />}
                                required
                            />

                            <div className="flex space-x-3 pt-4">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setShowForgotPassword(false)}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    loading={forgotPasswordLoading}
                                    className="flex-1"
                                >
                                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Email'}
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}

        </div>
    );
};

export default LoginForm;
